import { BookOpen, Dna, Microscope, ShieldCheck, Stethoscope } from 'lucide-react';
import type { ComponentType, ReactNode, SVGProps } from 'react';

import type { ChipIcon, EyebrowIcon, ValuePropCopy } from '../funnel-copy';

const CHIP_ICONS: Record<ChipIcon, ComponentType<SVGProps<SVGSVGElement>>> = {
  'book-open': BookOpen,
  'dna': Dna,
  'shield-check': ShieldCheck,
  'microscope': Microscope,
};

const EYEBROW_ICONS: Record<EyebrowIcon, ComponentType<SVGProps<SVGSVGElement>>> = {
  'stethoscope': Stethoscope,
};

interface ValuePropCardProps {
  copy: ValuePropCopy;
  /** Optional content rendered after the chips, separated by a horizontal divider.
   *  Used on Step 2 to fold the doctor quote into the same card as the eyebrow + title. */
  trailing?: ReactNode;
}

export function ValuePropCard({ copy, trailing }: ValuePropCardProps) {
  const EyebrowIconComponent = copy.eyebrowIcon ? EYEBROW_ICONS[copy.eyebrowIcon] : null;

  return (
    <div className="flex flex-col gap-3 rounded-2xl border-1 border-solid border-[#E3DCCE] bg-[#FBFAF7] px-6 py-5">
      {copy.eyebrow && (
        <div className="flex items-center gap-2">
          {EyebrowIconComponent && (
            <span
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-catch-dark-blue-90"
              aria-hidden="true"
            >
              <EyebrowIconComponent className="h-3.5 w-3.5" strokeWidth={2} />
            </span>
          )}
          <span className="text-xs font-bold uppercase tracking-widest text-gray-500">
            {copy.eyebrow}
          </span>
        </div>
      )}
      <h2 className="text-xl font-bold leading-snug text-catch-dark-blue-90">
        {copy.title}
      </h2>
      {copy.body && (
        <p className="text-base leading-relaxed text-gray-500">{copy.body}</p>
      )}
      {copy.chips.length > 0 && (
        <ul className="mt-1 flex flex-wrap items-center gap-2">
          {copy.chips.map((chip) => {
            const Icon = chip.icon ? CHIP_ICONS[chip.icon] : null;
            return (
              <li
                key={chip.label}
                className="inline-flex items-center gap-1.5 rounded-full border-1 border-solid border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-catch-dark-blue-90"
              >
                {Icon && <Icon className="h-4 w-4" aria-hidden="true" strokeWidth={2} />}
                {chip.label}
              </li>
            );
          })}
        </ul>
      )}
      {trailing && (
        <>
          <hr className="my-5 border-0 border-t-1 border-solid border-gray-200" />
          {trailing}
        </>
      )}
    </div>
  );
}
